import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import Sheet from '../../../components/Sheet'
import {
	createOwnAsset,
	deleteOwnAsset,
	invalidateMentionAssets,
	listAssets,
	parseAssetError,
	type Asset,
	type AssetKind,
	type AssetLink,
	type ScenePlan, tileCrop } from '../../../api/assets'
import { getScene, MAX_SCENE_ASSETS, requestSceneRemove, SCENE_PICKER_EVENT, type ScenePickerRequest } from '../../../api/sceneSelection'
import { openCharacterCreator } from '../../../utils/characterCreatorRequest'
import { useSceneTools } from '../../../hooks/useSceneTools'
import { useSourcePicker } from '../hooks/useSourcePicker'
import VoiceProbePill from '../../../components/voice/VoiceProbePill'
import { useVoiceProbe } from '../../../components/voice/useVoiceProbe'
import { linksRowWidth, splitLinks } from './pickerLinks'
import { KIND_ONE } from '../../assets/kinds'

/**
 * Пикер сцены (Ф2 «Библиотека сцены»): персонажи, локации и предметы — свои и из библиотеки — одним окном.
 * Вид повторяет окно выбора ассетов Artlist Studio (владелец 30.09: «визуалы почти идентичные конкурентам,
 * не считая кнопок и иконок»): мозаика персонажей в 4 колонки с плитками разной высоты, ряды «3 широких /
 * 4 узких» у локаций и предметов, бейдж библиотеки, «Выбрано» поверх плитки, предупреждение про нескольких
 * персонажей в подвале. Кнопки и иконки — наши.
 *
 * Открывается событием umnik:scene-picker-open (пункты «Персонаж / Локация / Предмет» в меню фото-слота);
 * хост живёт в UploadsRow и в пайпе тренда. Режим 'pipe' — слот героя тренда: только свои персонажи и
 * библиотечные, один, клик сразу применяет; с kind 'location' — так же одна локация («Где будет …» шота «Повторить
 * движение» в «Сцене», 11.10). Режим 'link' — страница ассета: выбрать, что связать с ним (набор),
 * без ведущих плиток и без самого ассета. В режиме 'scene' на плитке — кружки набора (связи ассета): наведение
 * раскрывает кружок пилюлей с картинкой крупнее, клик добавляет связанное в сцену рядом с персонажем.
 */

const KINDS: { key: AssetKind; tab: string; title: string; newLabel: string }[] = [
	{ key: 'character', tab: 'Аватары', title: 'Выберите или создайте аватар', newLabel: 'Новый аватар' },
	{ key: 'location', tab: 'Локации', title: 'Выберите или загрузите локацию', newLabel: 'Загрузить своё' },
	{ key: 'prop', tab: 'Предметы', title: 'Выберите или загрузите предмет', newLabel: 'Загрузить своё' },
]
const PAGE = 24
// Предел связей у одного ассета — как на сервере (services/asset_links.MAX_LINKS).
const MAX_LINKS = 12
// Своих локаций и предметов — до 100 каждого вида, сервер отдаёт по 60: дочитываем вторую страницу.
const MINE_PAGE = 60

async function listAllMine(kind: AssetKind): Promise<Asset[]> {
	const first = await listAssets({ scope: 'mine', kind, pageSize: MINE_PAGE })
	let items = first.items
	for (let page = 2; items.length < first.pagination.total && page <= 5; page++) {
		const next = await listAssets({ scope: 'mine', kind, pageSize: MINE_PAGE, page })
		if (!next.items.length) break
		const seen = new Set(items.map((a) => a.id))
		items = [...items, ...next.items.filter((a) => !seen.has(a.id))]
	}
	return items
}
// Пропорции плиток персонажей — набор Artlist (1:1, 4:5, 2:3, 4:7): мозаика с разной высотой, вид в полный
// рост прижат к верху — квадратная плитка читается портретом, высокая показывает фигуру.
const CHARACTER_ASPECTS = [1, 0.8, 2 / 3, 4 / 7]
const NEW_TILE_ASPECT = 164 / 214

function hashOf(id: string): number {
	let h = 0
	for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0
	return h
}
const aspectOf = (id: string) => CHARACTER_ASPECTS[hashOf(id) % CHARACTER_ASPECTS.length]

type Tile = { type: 'new' } | { type: 'upload' } | { type: 'asset'; asset: Asset }

function LinkThumb({ l }: { l: AssetLink }) {
	const src = l.avatar_preview_url || l.avatar_url
	return src
		? <img src={src} alt="" loading="lazy" decoding="async" />
		: <span aria-hidden="true">{l.name.slice(0, 1).toUpperCase()}</span>
}

/** Мозаика персонажей: плитки по колонкам, каждая — в самую короткую (как у Artlist). */
function masonry(tiles: Tile[], cols: number): Tile[][] {
	const out: Tile[][] = Array.from({ length: cols }, () => [])
	const heights = Array(cols).fill(0)
	for (const t of tiles) {
		const i = heights.indexOf(Math.min(...heights))
		out[i].push(t)
		heights[i] += 1 / (t.type === 'asset' ? aspectOf(t.asset.id) : NEW_TILE_ASPECT) + 0.05
	}
	return out
}

const EMPTY_LIB = { items: [] as Asset[], page: 1, total: 0 }

export default function ScenePickerHost({
	onConfirm,
	probe,
	onPipePick,
	pipeOnly = false,
	full = false,
}: {
	/** Применить набор (порядок — порядок выбора); prefer — выбор «лист ↔ фото»; kinds — тип каждого выбранного
	 *  (страница «Сцена» ставит чип в поле своего типа в структурном режиме). */
	onConfirm?: (ids: string[], prefer?: 'sheet' | 'photos', kinds?: Record<string, AssetKind>, initial?: string[]) => void
	/** План без применения: развилка «лист ↔ фото» у модели с одним слотом. */
	probe?: (ids: string[]) => Promise<ScenePlan | null>
	/** Слот героя тренда (персонаж) и выбор одной локации «Сцены»: один ассет, клик применяет сразу. */
	onPipePick?: (assetId: string) => void
	/** Хост пайпа: реагирует только на открытие в режиме 'pipe'. */
	pipeOnly?: boolean
	/** Страница «Сцена»: библиотека и все три вкладки открыты тому, кто вошёл в раздел, независимо от рубильника
	 *  генератора. */
	full?: boolean
}) {
	// Библиотека сцены закрыта этому пользователю (рубильник в админке): только свои персонажи, по одному — как было
	// до библиотеки. Готовые ассеты, локации и предметы не показываем; сервер их и не отдаст.
	const restricted = !useSceneTools() && !full
	const [open, setOpen] = useState(false)
	const [mode, setMode] = useState<'scene' | 'pipe' | 'link'>('scene')
	const [kind, setKind] = useState<AssetKind>('character')
	// Режим 'link': чей набор собираем (его в списке нет), какие вкладки спрятать, заголовок окна.
	const [exclude, setExclude] = useState<string | null>(null)
	const [hideKinds, setHideKinds] = useState<AssetKind[]>([])
	const [title, setTitle] = useState<string | null>(null)
	// Раскрытый кружок набора: «<ассет>:<связанный>» или «<ассет>:more». Пилюля рисуется порталом в body с
	// position: fixed — внутри списка её срезали прокрутка (у первого ряда) и край плитки (владелец 07.10). Якорь —
	// угол ряда кружков: side — к какому краю ряда прижата (у плиток первой колонки влево места нет — вправо),
	// dir — вверх от ряда или вниз, когда над кружками места не хватает (низкие плитки локаций в первом ряду).
	const [linkOpen, setLinkOpen] = useState<string | null>(null)
	const [pop, setPop] = useState<{ side: 'right' | 'left'; dir: 'up' | 'down'; x: number; y: number }>({ side: 'right', dir: 'up', x: 0, y: 0 })
	const popRef = useRef<HTMLDivElement>(null)
	// Пилюля не вложена в ряд в DOM: уход мыши с ряда закрывает её с задержкой, вход на пилюлю — отменяет.
	const closeTimer = useRef<number | null>(null)
	const cancelClose = () => { if (closeTimer.current) { window.clearTimeout(closeTimer.current); closeTimer.current = null } }
	const scheduleClose = () => { cancelClose(); closeTimer.current = window.setTimeout(() => setLinkOpen(null), 120) }
	useEffect(() => () => { if (closeTimer.current) window.clearTimeout(closeTimer.current) }, [])
	const POP_W = 240
	// Без наведения (телефон): первое касание кружка раскрывает пилюлю, второе — добавляет.
	const [touch] = useState(() => typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches)
	const [seed, setSeed] = useState(0)
	const [nonce, setNonce] = useState(0)
	const [mine, setMine] = useState<Partial<Record<AssetKind, Asset[]>>>({})
	const [lib, setLib] = useState<Partial<Record<AssetKind, { items: Asset[]; page: number; total: number }>>>({})
	// Ошибка первой загрузки вкладки и ошибка догрузки библиотеки — по типам: пока она стоит, страж прокрутки
	// не запрашивает снова (иначе при недоступном сервере запросы шли бы без остановки).
	const [errors, setErrors] = useState<Partial<Record<AssetKind, string>>>({})
	const [moreErrors, setMoreErrors] = useState<Partial<Record<AssetKind, string>>>({})
	const [moreLoading, setMoreLoading] = useState(false)
	const [picked, setPicked] = useState<string[]>([])
	const [meta, setMeta] = useState<Record<string, { kind: AssetKind; name: string }>>({})
	const [note, setNote] = useState<string | null>(null)
	const [choice, setChoice] = useState<{ ids: string[]; plan: ScenePlan } | null>(null)
	const [busy, setBusy] = useState(false)
	const [upload, setUpload] = useState<{ kind: 'location' | 'prop'; file: File; url: string; name: string; saving: boolean; error: string | null } | null>(null)
	const savingRef = useRef(false)
	const dialogRef = useRef<HTMLDivElement>(null)
	const openerRef = useRef<HTMLElement | null>(null)
	const [confirmDelete, setConfirmDelete] = useState<string | null>(null)
	const [mobile, setMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches)
	// «Загрузить своё» (локация, предмет) — стандартный выбор источника: с компьютера или из «Моих генераций».
	const source = useSourcePicker()
	const scrollRef = useRef<HTMLDivElement>(null)
	const sentinelRef = useRef<HTMLDivElement>(null)
	const initialRef = useRef<string[]>([])
	const navigate = useNavigate()

	useEffect(() => {
		const mq = window.matchMedia('(max-width: 768px)')
		const on = () => setMobile(mq.matches)
		mq.addEventListener('change', on)
		return () => mq.removeEventListener('change', on)
	}, [])

	// Открытие: сброс — в обработчике события (react-hooks/set-state-in-effect).
	useEffect(() => {
		const handler = (e: Event) => {
			const req = (e as CustomEvent<ScenePickerRequest>).detail || {}
			const m = req.mode ?? 'scene'
			if (pipeOnly !== (m === 'pipe')) return
			// Без библиотеки локаций и предметов нет — выбор одной локации («Где будет …») не открываем: вместо неё
			// пришёл бы персонаж.
			if (m === 'pipe' && restricted && req.kind && req.kind !== 'character') return
			const scene = getScene()
			// Начальный выбор: явный список из запроса (страница «Сцена») или сцена виджета.
			const seeded = req.initial ?? (scene?.assets ?? []).map((a) => ({ id: a.id, kind: a.kind, name: a.name }))
			// Без библиотеки в выборе может быть только один свой персонаж.
			const seed = restricted ? seeded.filter((a) => a.kind === 'character').slice(0, 1) : seeded
			const ids = m === 'pipe' ? [] : seed.map((a) => a.id)
			initialRef.current = ids
			setMode(m)
			setKind(restricted ? 'character' : req.kind ?? 'character')
			setExclude(req.excludeId ?? null)
			setHideKinds(req.hideKinds ?? [])
			setTitle(req.title ?? null)
			setLinkOpen(null)
			setPicked(ids)
			setMeta(Object.fromEntries(seed.map((a) => [a.id, { kind: a.kind, name: a.name }])))
			setMine({})
			setLib({})
			setErrors({})
			setMoreErrors({})
			setMoreLoading(false)
			setNote(null)
			setChoice(null)
			setUpload(null)
			setConfirmDelete(null)
			setSeed(Math.random() * 2 - 1)
			// Фокус вернём туда, откуда открыли (кнопка слота, пункт меню).
			openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
			setOpen(true)
			setNonce((n) => n + 1)
		}
		window.addEventListener(SCENE_PICKER_EVENT, handler)
		return () => window.removeEventListener(SCENE_PICKER_EVENT, handler)
	}, [pipeOnly, restricted])

	// Данные вкладки: свои (все) + первая страница библиотеки в случайном порядке этого открытия.
	useEffect(() => {
		if (!open) return
		let alive = true
		const k = kind
		const needMine = !mine[k]
		const needLib = !lib[k] && !restricted
		if ((!needMine && !needLib) || errors[k]) return
		Promise.all([
			needMine ? listAllMine(k) : null,
			needLib ? listAssets({ scope: 'library', kind: k, pageSize: PAGE, page: 1, seed }) : null,
		])
			.then(([m, l]) => {
				if (!alive) return
				if (m) setMine((p) => ({ ...p, [k]: m }))
				if (l) setLib((p) => ({ ...p, [k]: { items: l.items, page: 1, total: l.pagination.total } }))
			})
			.catch((e) => { if (alive) setErrors((p) => ({ ...p, [k]: parseAssetError(e) })) })
		return () => { alive = false }
	}, [open, kind, nonce, restricted]) // eslint-disable-line react-hooks/exhaustive-deps

	// «Загружаем» — пока данных вкладки нет и ошибки нет: отдельного флага, который мог бы залипнуть при смене
	// вкладки на середине запроса, нет.
	const error = errors[kind] ?? null
	const libK = restricted ? EMPTY_LIB : lib[kind]
	const loading = open && !error && (!mine[kind] || !libK)

	const retry = () => {
		setErrors((p) => ({ ...p, [kind]: undefined }))
		setNonce((n) => n + 1)
	}

	const loadMore = useCallback(() => {
		const cur = restricted ? null : lib[kind]
		if (!cur || moreLoading || moreErrors[kind] || cur.items.length >= cur.total) return
		const k = kind
		setMoreLoading(true)
		listAssets({ scope: 'library', kind: k, pageSize: PAGE, page: cur.page + 1, seed })
			.then((l) => setLib((p) => {
				const prev = p[k]
				if (!prev) return p
				const seen = new Set(prev.items.map((a) => a.id))
				const fresh = l.items.filter((a) => !seen.has(a.id))
				// Страница пришла пустой, хотя по счёту ассеты ещё есть (библиотеку меняли) — считаем список дочитанным.
				return { ...p, [k]: { items: [...prev.items, ...fresh], page: prev.page + 1, total: l.items.length ? l.pagination.total : prev.items.length } }
			}))
			.catch((e) => setMoreErrors((p) => ({ ...p, [k]: parseAssetError(e) })))
			.finally(() => setMoreLoading(false))
	}, [lib, kind, moreLoading, moreErrors, seed, restricted])

	// Бесконечная прокрутка библиотеки: страж внизу сетки.
	useEffect(() => {
		const el = sentinelRef.current
		if (!open || !el) return
		const io = new IntersectionObserver((entries) => { if (entries.some((e) => e.isIntersecting)) loadMore() }, { root: scrollRef.current, rootMargin: '400px' })
		io.observe(el)
		return () => io.disconnect()
	}, [open, loadMore, kind])

	// Пробы голосов персонажей (пилюля на плитке): одна фраза из админки каждым голосом, один голос за раз.
	const voiceProbe = useVoiceProbe()
	const stopVoice = voiceProbe.stop
	useEffect(() => () => stopVoice(), [stopVoice])
	const close = () => {
		setOpen(false)
		stopVoice()
		if (upload) URL.revokeObjectURL(upload.url)
		setUpload(null)
		const opener = openerRef.current
		openerRef.current = null
		if (opener?.isConnected) opener.focus({ preventScroll: true })
	}
	const closeRef = useRef(close)
	useEffect(() => { closeRef.current = close })

	// Окно на десктопе: фокус — внутрь при открытии, Escape закрывает откуда угодно (раньше — только если фокус
	// уже был внутри окна: сразу после открытия он оставался на кнопке слота, и Escape не срабатывал). Форма
	// «Загрузить своё» по Escape сперва закрывается сама.
	const uploadOpen = !!upload
	useEffect(() => {
		if (!open || mobile) return
		dialogRef.current?.focus({ preventScroll: true })
	}, [open, mobile])
	useEffect(() => {
		if (!open || mobile) return
		const onKey = (e: KeyboardEvent) => {
			if (e.key !== 'Escape' || e.defaultPrevented) return
			e.preventDefault()
			if (uploadOpen) cancelUploadRef.current()
			else closeRef.current()
		}
		window.addEventListener('keydown', onKey)
		return () => window.removeEventListener('keydown', onKey)
	}, [open, mobile, uploadOpen])

	const remember = (a: Asset) => setMeta((p) => ({ ...p, [a.id]: { kind: a.kind, name: a.name } }))

	/** Страница персонажа (как «New Character» у Artlist — ссылка на /character/create): в «Сцене» — здесь же, её
	 *  проект живёт на сервере; в генераторе — новой вкладкой, чтобы не потерять набранное. */
	const openPage = (path: string) => {
		close()
		if (full) navigate(path)
		else window.open(path, '_blank', 'noopener')
	}

	const toggle = (a: Asset) => {
		setNote(null)
		setLinkOpen(null)
		if (a.kind === 'character' && a.is_own && a.status !== 'ready') {
			// Персонаж без готового главного вида: не выбирается (в генерацию нечего отдать), но и не тупик —
			// его страница (как у Artlist) дорисует виды. В режиме связей со страницы не уходим.
			if (a.status === 'building' || mode === 'link') {
				setNote(a.status === 'building' ? 'Виды аватара ещё рисуются — вернитесь через минуту' : 'Аватар ещё не собран — связать можно готовый')
				return
			}
			openPage(`/characters/${a.id}`)
			return
		}
		remember(a)
		if (mode === 'pipe') {
			close()
			onPipePick?.(a.id)
			return
		}
		if (restricted) {
			// Один персонаж: второй клик по нему снимает выбор, клик по другому — заменяет.
			setPicked((p) => (p.includes(a.id) ? [] : [a.id]))
			return
		}
		if (mode === 'link') {
			if (!picked.includes(a.id) && picked.length >= MAX_LINKS) {
				setNote(`У одного аватара, локации или предмета до ${MAX_LINKS} связей — снимите лишние, чтобы добавить`)
				return
			}
			setPicked((p) => (p.includes(a.id) ? p.filter((x) => x !== a.id) : [...p, a.id]))
			return
		}
		if (!picked.includes(a.id) && picked.length >= MAX_SCENE_ASSETS) {
			setNote(`В сцене до ${MAX_SCENE_ASSETS} аватаров, локаций и предметов — снимите лишнее, чтобы добавить`)
			return
		}
		setPicked((p) => (p.includes(a.id) ? p.filter((x) => x !== a.id) : [...p, a.id]))
	}

	/** Кружок набора: связанное добавляется в сцену рядом с персонажем, повторный клик снимает. */
	const toggleLink = (l: AssetLink) => {
		setNote(null)
		setMeta((p) => ({ ...p, [l.id]: { kind: l.kind, name: l.name } }))
		if (!picked.includes(l.id) && picked.length >= MAX_SCENE_ASSETS) {
			setNote(`В сцене до ${MAX_SCENE_ASSETS} аватаров, локаций и предметов — снимите лишнее, чтобы добавить`)
			return
		}
		setPicked((p) => (p.includes(l.id) ? p.filter((x) => x !== l.id) : [...p, l.id]))
	}

	const same = picked.length === initialRef.current.length && picked.every((id, i) => id === initialRef.current[i])
	const kindsOf = (ids: string[]): Record<string, AssetKind> =>
		Object.fromEntries(ids.flatMap((id) => (meta[id]?.kind ? [[id, meta[id].kind]] : [])))

	const confirm = async () => {
		if (!onConfirm || busy) return
		if (same) {
			close()
			return
		}
		// Развилка «лист ↔ фото» бывает только у своего персонажа с листом-карточкой и моделью с одним слотом.
		const withSheet = picked.some((id) => [...(mine.character ?? [])].some((a) => a.id === id && a.has_sheet))
		if (probe && withSheet && mode === 'scene') {
			setBusy(true)
			try {
				const plan = await probe(picked)
				if (plan?.choice?.available) {
					setChoice({ ids: picked, plan })
					return
				}
			} catch (e) {
				setNote(parseAssetError(e))
				return
			} finally {
				setBusy(false)
			}
		}
		close()
		onConfirm(picked, undefined, kindsOf(picked), initialRef.current)
	}

	const onFile = (f: File | undefined) => {
		if (!f || (kind !== 'location' && kind !== 'prop')) return
		const base = f.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').trim().slice(0, 60)
		if (upload) URL.revokeObjectURL(upload.url)
		// Тип запоминаем в форме: сохранится тем, под какой вкладкой выбрали файл.
		setUpload({ kind, file: f, url: URL.createObjectURL(f), name: base || (kind === 'location' ? 'Моя локация' : 'Мой предмет'), saving: false, error: null })
	}

	const cancelUpload = () => {
		if (!upload || upload.saving) return
		URL.revokeObjectURL(upload.url)
		setUpload(null)
	}
	const cancelUploadRef = useRef(cancelUpload)
	useEffect(() => { cancelUploadRef.current = cancelUpload })

	const saveUpload = async () => {
		// savingRef — против второго Enter в тот же тик: состояние saving к нему ещё не обновилось, и создавались
		// два одинаковых ассета.
		if (!upload || savingRef.current) return
		const name = upload.name.trim()
		if (!name) {
			setUpload({ ...upload, error: 'Дайте название — по нему вы позовёте это в промпте через «@»' })
			return
		}
		savingRef.current = true
		setUpload({ ...upload, saving: true, error: null })
		try {
			const a = await createOwnAsset(upload.kind, name, upload.file)
			invalidateMentionAssets()
			URL.revokeObjectURL(upload.url)
			setUpload(null)
			setMine((p) => ({ ...p, [a.kind]: [a, ...(p[a.kind] ?? [])] }))
			remember(a)
			// Новое сразу отмечаем выбранным — если в сцене ещё есть место.
			setPicked((p) => (p.includes(a.id) || p.length >= MAX_SCENE_ASSETS ? p : [...p, a.id]))
		} catch (e) {
			setUpload((u) => (u ? { ...u, saving: false, error: parseAssetError(e) } : u))
		} finally {
			savingRef.current = false
		}
	}

	/** «Новый персонаж» — общее окно создания (владелец 10.10: «из любого отдела»), а не уход на страницу раздела.
	 *  Слот героя (pipe) — окно вместо пикера, созданный сразу в слот; сцена — пикер ждёт под окном, созданный —
	 *  первым в «своих» и выбран (если в сцене есть место). */
	const createNew = async () => {
		const pipe = mode === 'pipe'
		if (pipe) close()
		const a = await openCharacterCreator({ start: 'choose' })
		if (!a) return
		invalidateMentionAssets()
		if (pipe) { onPipePick?.(a.id); return }
		setMine((p) => ({ ...p, character: [a, ...(p.character ?? []).filter((x) => x.id !== a.id)] }))
		remember(a)
		setPicked((p) => {
			if (restricted) return [a.id]
			return p.includes(a.id) || p.length >= MAX_SCENE_ASSETS ? p : [...p, a.id]
		})
	}

	const removeOwn = async (a: Asset) => {
		try {
			await deleteOwnAsset(a.id)
			invalidateMentionAssets()
			setMine((p) => ({ ...p, [a.kind]: (p[a.kind] ?? []).filter((x) => x.id !== a.id) }))
			setPicked((p) => p.filter((x) => x !== a.id))
			initialRef.current = initialRef.current.filter((x) => x !== a.id)
			// Удалённое уже стояло в сцене виджета — снимаем сразу, не дожидаясь «Готово»: иначе после «Отмена»
			// в промпте остался бы чип, а в слотах — фото того, чего больше нет.
			if (getScene()?.assets.some((x) => x.id === a.id)) requestSceneRemove(a.id)
		} catch (e) {
			setNote(parseAssetError(e))
		} finally {
			setConfirmDelete(null)
		}
	}

	const k = KINDS.find((x) => x.key === kind) ?? KINDS[0]
	const list = useMemo(() => {
		const own = mine[kind] ?? []
		const ownIds = new Set(own.map((a) => a.id))
		// Свои — первыми (готовые раньше черновиков), дальше библиотека в случайном порядке этого открытия.
		const ownSorted = [...own].sort((a, b) => Number(b.status === 'ready') - Number(a.status === 'ready'))
		return [...ownSorted, ...(libK?.items ?? []).filter((a) => !ownIds.has(a.id))].filter((a) => a.id !== exclude)
	}, [mine, libK, kind, exclude])

	const lead: Tile = kind === 'character' ? { type: 'new' } : { type: 'upload' }
	// Ведущая плитка: в сцене — «Новый персонаж» / «Загрузить своё», в слоте героя (pipe) — «Новый персонаж».
	const withLead = mode === 'scene' || (mode === 'pipe' && kind === 'character')
	const tiles: Tile[] = [...(withLead ? [lead] : []), ...list.map((a) => ({ type: 'asset' as const, asset: a }))]
	const tabs = KINDS.filter((x) => !hideKinds.includes(x.key))
	const heading = mode === 'pipe' ? (kind === 'location' ? 'Выберите локацию' : kind === 'prop' ? 'Выберите предмет' : 'Выберите аватар')
		: mode === 'link' ? title ?? 'Связать' : k.title

	// Кружки набора — справа на линии голоса. Наведение (на телефоне — касание) раскрывает кружок пилюлей с
	// картинкой крупнее, именем и типом; клик — в сцену. Больше четырёх — «+N» со списком остальных.
	const renderLinks = (a: Asset) => {
		const links = a.links ?? []
		if (!links.length) return null
		const { shown, rest } = splitLinks(links)
		const keyOf = (id: string) => `${a.id}:${id}`
		const openKey = linkOpen && linkOpen.startsWith(`${a.id}:`) ? linkOpen : null
		const openLink = openKey ? links.find((l) => keyOf(l.id) === openKey) ?? null : null
		const moreOpen = openKey === keyOf('more')
		const openAt = (el: HTMLElement, key: string) => {
			cancelClose()
			const row = el.closest('.scene-pick__links')?.getBoundingClientRect()
			const box = scrollRef.current?.getBoundingClientRect()
			if (row) {
				const side = box && row.right - POP_W < box.left + 8 ? 'left' : 'right'
				const need = key.endsWith(':more') ? Math.min(rest.length, 8) * 40 + 24 : 92
				const dir = row.top - (box?.top ?? 0) >= need ? 'up' : 'down'
				setPop({ side, dir, x: side === 'left' ? row.left : row.right, y: dir === 'up' ? row.top : row.bottom })
			}
			setLinkOpen(key)
		}
		const press = (el: HTMLElement, key: string, l: AssetLink | null) => {
			if (touch && linkOpen !== key) { openAt(el, key); return }
			if (l) toggleLink(l)
			else openAt(el, key)
		}
		return (
			<div className={`scene-pick__links${openKey ? ' is-open' : ''}`} role="group" aria-label={`Набор «${a.name}»`}
				onMouseEnter={cancelClose} onMouseLeave={() => { if (!touch) scheduleClose() }}
				onBlur={(e) => { const to = e.relatedTarget as Node | null; if (!e.currentTarget.contains(to) && !popRef.current?.contains(to)) setLinkOpen(null) }}>
				{shown.map((l) => {
					const on = picked.includes(l.id)
					const key = keyOf(l.id)
					return (
						<button key={l.id} type="button" className={`scene-pick__link${on ? ' is-on' : ''}${linkOpen === key ? ' is-open' : ''}`}
							aria-pressed={on} aria-label={`${KIND_ONE[l.kind]} ${l.name}: ${on ? 'убрать из сцены' : 'добавить в сцену'}`}
							onMouseEnter={(e) => { if (!touch) openAt(e.currentTarget, key) }} onFocus={(e) => openAt(e.currentTarget, key)}
							onClick={(e) => { e.stopPropagation(); press(e.currentTarget, key, l) }}>
							<LinkThumb l={l} />
						</button>
					)
				})}
				{rest.length > 0 && (
					<button type="button" className={`scene-pick__link scene-pick__link--more${moreOpen ? ' is-open' : ''}`}
						aria-label={`Ещё ${rest.length} из набора`} aria-expanded={moreOpen}
						onMouseEnter={(e) => { if (!touch) openAt(e.currentTarget, keyOf('more')) }} onFocus={(e) => openAt(e.currentTarget, keyOf('more'))}
						onClick={(e) => { e.stopPropagation(); openAt(e.currentTarget, keyOf('more')) }}>
						+{rest.length}
					</button>
				)}
				{(openLink || moreOpen) && createPortal(
					<div ref={popRef} className={`scene-pick__pop is-${pop.side} is-${pop.dir}`} style={{ left: pop.x, top: pop.y }}
						onMouseEnter={cancelClose} onMouseLeave={() => { if (!touch) scheduleClose() }}
						onBlur={(e) => { const to = e.relatedTarget as Node | null; if (!e.currentTarget.contains(to) && !(to as Element | null)?.closest?.('.scene-pick__links')) setLinkOpen(null) }}>
				{openLink && (
					<button type="button" className={`scene-pick__link-pop${picked.includes(openLink.id) ? ' is-on' : ''}`}
						onClick={(e) => { e.stopPropagation(); toggleLink(openLink) }}>
						<span className="scene-pick__link-pop-img"><LinkThumb l={openLink} /></span>
						<span className="scene-pick__link-pop-text">
							<b>{openLink.name}</b>
							<i>{KIND_ONE[openLink.kind]}</i>
							<em>{picked.includes(openLink.id) ? 'В сцене — убрать' : 'Добавить в сцену'}</em>
						</span>
					</button>
				)}
				{moreOpen && (
					<div className="scene-pick__link-pop scene-pick__link-pop--list" role="group" aria-label="Ещё из набора">
						{rest.map((l) => {
							const on = picked.includes(l.id)
							return (
								<button key={l.id} type="button" className={`scene-pick__link-row${on ? ' is-on' : ''}`} aria-pressed={on}
									onClick={(e) => { e.stopPropagation(); toggleLink(l) }}>
									<span className="scene-pick__link-row-img"><LinkThumb l={l} /></span>
									<span className="scene-pick__link-row-text"><b>{l.name}</b><i>{KIND_ONE[l.kind]}</i></span>
									<em aria-hidden="true">{on ? '✓' : '+'}</em>
								</button>
							)
						})}
					</div>
				)}
					</div>,
					document.body,
				)}
			</div>
		)
	}
	const pickedByKind = (kk: AssetKind) => picked.filter((id) => meta[id]?.kind === kk).length
	const characters = pickedByKind('character')

	const renderTile = (t: Tile, key: string, extra?: { wide?: boolean; aspect?: number }) => {
		if (t.type === 'new') {
			return (
				<button key={key} type="button" className="scene-pick__tile scene-pick__tile--lead" style={{ aspectRatio: String(NEW_TILE_ASPECT) }}
					onClick={() => void createNew()}>
					<span className="scene-pick__lead-plus" aria-hidden="true">
						<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
					</span>
					<span className="scene-pick__lead-text">{k.newLabel}</span>
				</button>
			)
		}
		if (t.type === 'upload') {
			const aspect = extra?.aspect ?? 16 / 9
			if (upload && upload.kind === kind) {
				return (
					<div key={key} className={`scene-pick__tile scene-pick__tile--form${extra?.wide ? ' is-wide' : ''}`} style={{ aspectRatio: String(aspect) }}>
						<img src={upload.url} alt="" className="scene-pick__img" />
						<div className="scene-pick__form">
							<input
								className="scene-pick__form-input"
								value={upload.name}
								maxLength={60}
								autoFocus
								aria-label="Название"
								placeholder="Название"
								onChange={(e) => setUpload({ ...upload, name: e.target.value, error: null })}
								onKeyDown={(e) => { if (e.key === 'Enter') void saveUpload() }}
							/>
							<div className="scene-pick__form-row">
								<button type="button" className="scene-pick__form-btn scene-pick__form-btn--ghost" disabled={upload.saving}
									onClick={cancelUpload}>Отмена</button>
								<button type="button" className="scene-pick__form-btn" disabled={upload.saving} onClick={() => void saveUpload()}>
									{upload.saving ? 'Сохраняем…' : 'Сохранить'}
								</button>
							</div>
							{upload.error && <div className="scene-pick__form-err">{upload.error}</div>}
						</div>
					</div>
				)
			}
			return (
				<button key={key} type="button" className={`scene-pick__tile scene-pick__tile--lead${extra?.wide ? ' is-wide' : ''}`} style={{ aspectRatio: String(aspect) }}
					aria-haspopup="menu" onClick={(e) => source.open(e.currentTarget, { kind: 'images', onFile: (f) => onFile(f) })}>
					<span className="scene-pick__lead-plus" aria-hidden="true">
						<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
							<path d="M12 16V4M7 9l5-5 5 5" /><path d="M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" />
						</svg>
					</span>
					<span className="scene-pick__lead-text">{k.newLabel}</span>
					<span className="scene-pick__lead-sub">своё фото — без генерации</span>
				</button>
			)
		}
		const a = t.asset
		const on = picked.includes(a.id)
		const draft = a.kind === 'character' && a.is_own && a.status !== 'ready'
		const { src: thumb, top } = tileCrop(a)  // свой аватар — верх кадра до груди, как плитки галереи
		const aspect = extra?.aspect ?? aspectOf(a.id)
		const withLinks = mode === 'scene' && !restricted && !!a.links?.length
		const { shown: shownLinks, rest: restLinks } = withLinks ? splitLinks(a.links ?? []) : { shown: [], rest: [] }
		// До двух кружков — на линии голоса справа (пилюля голоса ужимается на их ширину); три и больше — строкой выше,
		// иначе от имени голоса оставалось «Ne…».
		const circles = shownLinks.length + (restLinks.length ? 1 : 0)
		const manyLinks = withLinks && circles > 2
		return (
			<div key={key} className={`scene-pick__cell${extra?.wide ? ' is-wide' : ''}${withLinks ? ' has-links' : ''}${manyLinks ? ' has-links-many' : ''}`}
				style={withLinks && !manyLinks ? { ['--links-w' as string]: `${linksRowWidth(circles) + 6}px` } : undefined}>
				<button
					type="button"
					className={`scene-pick__tile${on ? ' is-on' : ''}${draft ? ' is-draft' : ''}${top ? ' scene-pick__tile--top' : ''}`}
					style={{ aspectRatio: String(aspect) }}
					aria-pressed={on}
					aria-label={draft ? `Дособрать аватар: ${a.name}` : `${on ? 'Снять' : 'Выбрать'}: ${a.name}`}
					onClick={() => toggle(a)}
				>
					{thumb ? (
						<img
							src={thumb}
							alt=""
							loading="lazy"
							decoding="async"
							className="scene-pick__img"
							onError={(e) => {
								// Превью нет (старое фото) — оригинал.
								const img = e.currentTarget
								if (a.avatar_url && img.src !== a.avatar_url) img.src = a.avatar_url
							}}
						/>
					) : (
						<span className="scene-pick__letter" aria-hidden="true">{a.name.slice(0, 1).toUpperCase()}</span>
					)}
					<span className="scene-pick__shade" aria-hidden="true" />
					{a.is_stock && <span className="scene-pick__badge">Umnik AI</span>}
					{draft && <span className="scene-pick__badge scene-pick__badge--draft">{a.status === 'building' ? 'собираем…' : 'не собран'}</span>}
					{/* Свой собранный персонаж — тот же бейдж готовности, что в кабинете (единый язык статуса). */}
					{a.kind === 'character' && a.is_own && !draft && (
						<span className="char-picker__ready scene-pick__ready" title="Аватар собран — готов к работе" aria-label="Готов">
							<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
								<path d="M5 13l4 4L19 7" />
							</svg>
						</span>
					)}
					<span className="scene-pick__name">{a.name}</span>
					<span className="scene-pick__sel" aria-hidden="true">
						<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M19 7.2 9.4 16.8 5 12.4" /></svg>
						Выбрано
					</span>
				</button>
				{/* Голос персонажа — над именем: ▶, нажатие играет пробу (плитку не выбирает); чей голос и какой — в подсказке
				    при наведении (08.10). Без голоса — приглушённый значок; голос задаётся на странице персонажа. Пилюля общая
				    со списком стопки и окном звука композера; позицию на плитке держит обёртка подсказки. */}
				{a.kind === 'character' && <VoiceProbePill voice={a.voice} characterId={a.id} characterName={a.name} probe={voiceProbe} wrapClassName="scene-pick__voice-wrap" />}
				{withLinks && renderLinks(a)}
				{a.is_own && a.kind !== 'character' && (
					confirmDelete === a.id ? (
						<span className="scene-pick__del-confirm">
							<button type="button" onClick={() => void removeOwn(a)}>Удалить</button>
							<button type="button" onClick={() => setConfirmDelete(null)}>Оставить</button>
						</span>
					) : (
						<button type="button" className="scene-pick__del" aria-label={`Удалить «${a.name}»`} title="Удалить" onClick={() => setConfirmDelete(a.id)}>
							<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
								<path d="M4 7h16M10 11v6M14 11v6M6 7l1 12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-12M9 7V4h6v3" />
							</svg>
						</button>
					)
				)}
			</div>
		)
	}

	// Сетка: персонажи — мозаика колонками; локации и предметы — ряды «3 широких / 4 узких» (на телефоне — 2 в ряд).
	const grid = (() => {
		if (kind === 'character') {
			const cols = masonry(tiles, mobile ? 2 : 4)
			return (
				<div className="scene-pick__masonry">
					{cols.map((col, i) => (
						<div key={i} className="scene-pick__col">
							{col.map((t, j) => renderTile(t, t.type === 'asset' ? t.asset.id : `${t.type}-${i}-${j}`))}
						</div>
					))}
				</div>
			)
		}
		const wideAspect = kind === 'location' ? 221 / 124 : 221 / 147
		const narrowAspect = kind === 'location' ? 164 / 105 : 164 / 137
		if (mobile) {
			return (
				<div className="scene-pick__rows scene-pick__rows--mobile">
					{tiles.map((t, i) => renderTile(t, t.type === 'asset' ? t.asset.id : `${t.type}-${i}`, { aspect: wideAspect }))}
				</div>
			)
		}
		const rows: Tile[][] = []
		let i = 0
		let wide = true
		while (i < tiles.length) {
			const n = wide ? 3 : 4
			rows.push(tiles.slice(i, i + n))
			i += n
			wide = !wide
		}
		return (
			<div className="scene-pick__rows">
				{rows.map((row, r) => (
					<div key={r} className={`scene-pick__row${r % 2 === 0 ? ' is-wide' : ''}`}>
						{row.map((t, j) => renderTile(t, t.type === 'asset' ? t.asset.id : `${t.type}-${r}-${j}`, { wide: r % 2 === 0, aspect: r % 2 === 0 ? wideAspect : narrowAspect }))}
					</div>
				))}
			</div>
		)
	})()

	const empty = !loading && !error && list.length === 0

	const body = choice ? (
		<div className="char-choice scene-pick__choice">
			<p className="char-choice__title">Как вставить аватар в эту модель?</p>
			<div className="char-choice__tiles">
				<button type="button" className="char-choice__tile" onClick={() => { close(); onConfirm?.(choice.ids, 'sheet', kindsOf(choice.ids), initialRef.current) }}>
					{choice.plan.choice?.recommended === 'sheet' && <span className="char-choice__rec">рекомендуем</span>}
					{choice.plan.choice?.used === 'sheet' && choice.plan.layout[0]?.url
						? <img src={choice.plan.layout[0].preview_url || choice.plan.layout[0].url} alt="" loading="lazy" onError={(e) => { const u = choice.plan.layout[0]?.url; if (u) e.currentTarget.src = u }} />
						: <span className="char-choice__ph" aria-hidden="true">▦</span>}
					<b>Лист-карточка</b>
					<span>{choice.plan.choice?.why}</span>
				</button>
				<button type="button" className="char-choice__tile" onClick={() => { close(); onConfirm?.(choice.ids, 'photos', kindsOf(choice.ids), initialRef.current) }}>
					{choice.plan.choice?.recommended === 'photos' && <span className="char-choice__rec">рекомендуем</span>}
					{(() => {
						const hero = choice.plan.assets.find((a) => a.kind === 'character')
						const u = hero ? hero.avatar_preview_url || hero.avatar_url : null
						return u ? <img src={u} alt="" loading="lazy" /> : <span className="char-choice__ph" aria-hidden="true">◉</span>
					})()}
					<b>Обычный портрет</b>
					<span>Одно фото как есть — без коллажа ракурсов.</span>
				</button>
			</div>
			<button type="button" className="char-choice__back" onClick={() => setChoice(null)}>← К выбору</button>
		</div>
	) : (
		<>
			{mode !== 'pipe' && !restricted && (
				<div className="scene-pick__tabs" role="tablist" aria-label="Что выбрать: аватары, локации или предметы">
					{tabs.map((x) => {
						const n = pickedByKind(x.key)
						return (
							<button key={x.key} type="button" role="tab" aria-selected={kind === x.key}
								className={`scene-pick__tab${kind === x.key ? ' is-active' : ''}`}
								onClick={() => {
									if (x.key === kind) return
									// Незаконченная форма «Загрузить своё» закрывается: под другой вкладкой её не видно.
									cancelUpload()
									stopVoice()
									setKind(x.key)
									setNote(null)
									setLinkOpen(null)
									setConfirmDelete(null)
									scrollRef.current?.scrollTo({ top: 0 })
								}}>
								{x.tab}
								{n > 0 && <span className="scene-pick__tab-n">{n}</span>}
							</button>
						)
					})}
				</div>
			)}
			<div ref={scrollRef} className="scene-pick__scroll" onScroll={() => { if (linkOpen) setLinkOpen(null) }}>
				{error && (
					<p className="scene-pick__note scene-pick__note--warn">
						Не удалось загрузить: {error}{' '}
						<button type="button" className="scene-pick__retry" onClick={retry}>Повторить</button>
					</p>
				)}
				{note && <p className="scene-pick__note scene-pick__note--warn" role="status">{note}</p>}
				{grid}
				{empty && mode === 'pipe' && (
					<p className="scene-pick__note">{kind === 'character' ? 'Пока нет аватаров — создайте первый: «Новый аватар».' : kind === 'location' ? 'Пока нет локаций.' : 'Пока нет предметов.'}</p>
				)}
				{(loading || moreLoading) && <p className="scene-pick__note">Загружаем…</p>}
				{moreErrors[kind] && (
					<p className="scene-pick__note scene-pick__note--warn">
						Не удалось загрузить остальное: {moreErrors[kind]}{' '}
						<button type="button" className="scene-pick__retry" onClick={() => setMoreErrors((p) => ({ ...p, [kind]: undefined }))}>Повторить</button>
					</p>
				)}
				<div ref={sentinelRef} className="scene-pick__sentinel" aria-hidden="true" />
			</div>
			{mode !== 'pipe' && (
				<div className="scene-pick__foot">
					<div className="scene-pick__warn" aria-live="polite">
						{mode === 'scene' && characters > 1 && (
							<>
								<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
									<circle cx="12" cy="12" r="9" /><path d="M12 7.5v5.5" /><path d="M12 16.5h.01" />
								</svg>
								<span>Несколько аватаров в одном кадре могут хуже держать сходство.</span>
							</>
						)}
					</div>
					<button type="button" className="scene-pick__btn scene-pick__btn--ghost" onClick={close}>Отмена</button>
					<button type="button" className="scene-pick__btn" disabled={busy} onClick={() => void confirm()}>
						{busy ? 'Проверяем…' : same ? 'Готово' : mode === 'link' ? (picked.length ? `Связать · ${picked.length}` : 'Убрать все связи')
							: restricted ? (picked.length ? 'Выбрать' : 'Убрать аватар') : picked.length ? `В сцену · ${picked.length}` : 'Очистить сцену'}
					</button>
				</div>
			)}
		</>
	)

	const fileInput = source.nodes

	if (mobile) {
		return (
			<>
				<Sheet variant="bottom" showHandle open={open} onClose={close} title={mode === 'pipe' ? (kind === 'location' ? 'Локация' : kind === 'prop' ? 'Предмет' : 'Аватар') : heading}>
					<div className="scene-pick scene-pick--sheet">{body}</div>
				</Sheet>
				{fileInput}
			</>
		)
	}

	if (!open) return null
	return createPortal(
		<>
			<div className="scene-pick__backdrop" role="dialog" aria-modal="true" aria-label={heading} onClick={close}>
				<div ref={dialogRef} tabIndex={-1} className="scene-pick" onClick={(e) => e.stopPropagation()}>
					<div className="scene-pick__head">
						<h2 className="scene-pick__title">{heading}</h2>
						<button type="button" className="scene-pick__close" aria-label="Закрыть" onClick={close}>
							<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>
						</button>
					</div>
					{body}
				</div>
			</div>
			{fileInput}
		</>,
		document.body,
	)
}
